import { CorpusId } from '../models/aliases';

import { Button, IconButton, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import styled, { css, keyframes } from 'styled-components';

interface SelectedActionsPanelProps {
  selectedPapers: Array<CorpusId>;
  isReadOnly?: boolean;
  onClearAllSelectedClicked?: () => void;
  onExportSelectedClicked?: () => void;
}

export function SelectedActionBar(
  props: SelectedActionsPanelProps,
): JSX.Element {
  const {
    selectedPapers,
    isReadOnly = false,
    onClearAllSelectedClicked = () => {},
    onExportSelectedClicked = () => {},
  } = props;

  if (selectedPapers.length === 0) {
    return <></>;
  }

  return (
    <Panel>
      <NumSelectedLabel>
        {selectedPapers.length.toLocaleString()}{' '}
        {selectedPapers.length > 1 ? 'Papers' : 'Paper'} Selected
      </NumSelectedLabel>
      {!isReadOnly && (
        <TextActionButton onClick={onClearAllSelectedClicked}>
          Clear Selection
        </TextActionButton>
      )}
      <Grow />
      <ExportButton onClick={onExportSelectedClicked}>
        <DownloadIcon />
        Export Selected Citations
      </ExportButton>
    </Panel>
  );
}

const fadeInKeyFrames = keyframes`
  0% { opacity: 0%; }
  50% { opacity: 0%; }
  100% { opacity: 100%; }
`;

const fadeInCssSnippet = css`
  animation-name: ${fadeInKeyFrames};
  animation-duration: 0.3s;
  animation-timing-function: ease-in;
  animation-iteration-count: 1;
`;

const NumSelectedLabel = styled(Typography)`
  flex-grow: 0;

  user-select: none;
  -webkit-user-select: none;
  &.MuiTypography-root {
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }

  ${fadeInCssSnippet}
`;

const TextActionButton = styled(Button)`
  &.MuiButtonBase-root.MuiButton-root {
    padding: 4px;
    margin: 2px 16px 0px 16px;
    font-weight: 400;
    white-space: nowrap;
  }

  ${fadeInCssSnippet}
`;

const ExportButton = styled(IconButton)`
  padding: 0;
  &.MuiButtonBase-root .MuiSvgIcon-root {
    margin-right: 4px;
    width: 18px;
    height: 18px;
  }

  ${fadeInCssSnippet}

  ${({ theme }) => theme.breakpoints.down('md')} {
    &.MuiButtonBase-root {
      display: none;
    }
  }
`;

const openPanelKeyFrames = keyframes`
  0% { scaleY: 0%; }
  100% { scaleY: 100%; }
`;

const Panel = styled.div`
  display: flex;
  width: 100%;
  height: 44px;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: start;
  align-items: center;

  background-color: rgba(250, 242, 233, 0.1);

  animation-name: ${openPanelKeyFrames};
  animation-duration: 0.3s;
  animation-timing-function: ease-in;
  animation-iteration-count: 1;

  padding: 0px 24px;
  gap: 5px;
`;

const Grow = styled.div`
  flex-grow: 1;
`;
